import { useCallback, useEffect, useState } from 'react';
import { useQuery } from '@apollo/client';
import { TIKTOK_CLIP_PREVIEW } from '@/queries/publishing';

interface TikTokClipPreviewData {
  finished_video_url?: string | null;
  finished_video_thumbnail_url?: string | null;
  watermark?: boolean | null;
}

/**
 * The rendered video a TikTok dialog previews, whether it carries the
 * AutoClipper watermark, and its duration once the preview's metadata loads.
 */
export function useTikTokClipPreview({ open, clipId }: { open: boolean; clipId: string }) {
  const [durationSeconds, setDurationSeconds] = useState<number | null>(null);

  useEffect(() => {
    setDurationSeconds(null);
  }, [clipId, open]);

  const { data } = useQuery(TIKTOK_CLIP_PREVIEW, {
    variables: { id: clipId },
    skip: !open || !clipId,
  });
  const clip = data?.myClip as TikTokClipPreviewData | undefined;

  const onDuration = useCallback((seconds: number) => {
    setDurationSeconds(Number.isFinite(seconds) && seconds > 0 ? seconds : null);
  }, []);

  return {
    videoUrl: clip?.finished_video_url || null,
    posterUrl: clip?.finished_video_thumbnail_url || null,
    watermark: clip?.watermark === true,
    durationSeconds,
    onDuration,
  };
}

export type TikTokClipPreviewState = ReturnType<typeof useTikTokClipPreview>;
